import {
  BookmarkSimple,
  ChartBarHorizontal,
  ClipboardText,
  FlagBanner,
  Kanban,
  ListBullets,
  ListNumbers,
  Stack,
  Textbox,
} from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import { useEffect } from "react";
import { openSaveView, openViewsMenu } from "@/components/views/viewMeta";
import type { Product } from "@/data/types";
import { registerCommands } from "@/lib/commands";
import type { ProductSection } from "./ProductHeader";
import { copyRoadmap } from "./roadmap";

/**
 * Product-scoped commands while a product page is open: G then L / B / R / N to list, board,
 * timeline (roadmap) and Next up; V opens the views menu; Save view, Milestones, Fields and the
 * ROADMAP.md copy from the palette.
 */
export function useProductCommands(product: Product, section: ProductSection) {
  const key = product.key;
  useEffect(() => {
    const nav = () => m.palette_group_navigate();
    const view = () => m.palette_group_view();
    const items = section === "items";
    return registerCommands([
      {
        id: "product.list",
        title: () => m.cmd_go_list(),
        group: nav,
        icon: ListBullets,
        keys: ["g", "l"],
        run: (c) => c.navigate(`/p/${key}/list`),
      },
      {
        id: "product.board",
        title: () => m.cmd_go_board(),
        group: nav,
        icon: Kanban,
        keys: ["g", "b"],
        run: (c) => c.navigate(`/p/${key}/board`),
      },
      {
        id: "product.timeline",
        title: () => m.cmd_go_timeline(),
        group: nav,
        icon: ChartBarHorizontal,
        keys: ["g", "r"],
        run: (c) => c.navigate(`/p/${key}/timeline`),
      },
      {
        id: "product.next-up",
        title: () => m.cmd_go_next_up(),
        group: nav,
        icon: ListNumbers,
        keys: ["g", "n"],
        run: (c) => c.navigate(`/p/${key}/next-up`),
      },
      {
        id: "product.milestones",
        title: () => m.cmd_go_milestones(),
        group: nav,
        icon: FlagBanner,
        run: (c) => c.navigate(`/p/${key}/milestones`),
      },
      {
        id: "product.fields",
        title: () => m.cmd_go_fields(),
        group: nav,
        icon: Textbox,
        run: (c) => c.navigate(`/p/${key}/fields`),
      },
      ...(items
        ? [
            {
              id: "views.menu",
              title: () => m.cmd_views_menu(),
              group: view,
              icon: Stack,
              keys: ["v"],
              run: () => openViewsMenu(),
            },
            {
              id: "views.save",
              title: () => m.views_save(),
              group: view,
              icon: BookmarkSimple,
              run: () => openSaveView({ mode: "create" }),
            },
          ]
        : []),
      {
        id: "roadmap.copy",
        title: () => m.roadmap_copy(),
        group: () => m.palette_group_actions(),
        icon: ClipboardText,
        run: () => void copyRoadmap(key),
      },
    ]);
  }, [key, section]);
}
